<script setup>
  import { ref, onMounted } from 'vue';
  import FormInput from '~/components/form/FormInput.vue';
  import FormError from '~/components/form/FormError.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';

  const props = defineProps({
    title: {
      type: String,
      default: 'subscription',
    },
    email: {
      type: String,
      default: '',
    },
    remember: Boolean,
  });

  const username = ref('');
  const usernameError = ref('');
  const password = ref('');
  const passwordError = ref('');
  const internalRemember = ref(false);
  const responseError = ref('');
  const csrfToken = ref(
    document.querySelector('meta[name="csrf-token"]').getAttribute('content')
  );
  const isSending = ref(false);

  onMounted(() => {
    username.value = props.email;
    internalRemember.value = props.remember;
  });

  async function login() {
    isSending.value = true;
    validateForm();
    if (usernameError.value || passwordError.value) return;

    try {
      const { data } = await axios.post('/login', {
        login: username.value,
        password: password.value,
        remember: internalRemember.value,
        _token: csrfToken.value,
      });
      if (data.has_redirect) {
        window.location.href = data.redirect_url;
        return;
      }
      window.location.reload();
    } catch (err) {
      responseError.value = err?.response?.data?.message
        ? err.response.data.message
        : 'Something went wrong, please contact support.';
      isSending.value = false;
    }
  }

  function validateForm() {
    resetErrors();
    validateUsername();
    validatePassword();
  }
  function validateUsername() {
    if (!username.value) {
      usernameError.value = 'An email address is required.';
      isSending.value = false;
      return;
    }

    usernameError.value = '';
  }
  function validatePassword() {
    if (!password.value) {
      passwordError.value = 'A password is required.';
      isSending.value = false;
      return;
    }

    passwordError.value = '';
  }
  function resetErrors() {
    usernameError.value = '';
    passwordError.value = '';
    responseError.value = '';
  }
</script>

<template>
  <section class="site-login">
    <aside class="site-login__description">
      <p>
        You are not logged in. Please log in to access your {{ props.title }}.
      </p>
    </aside>
    <article class="site-login__article">
      <div class="site-login__auth-container">
        <h1>Sign In</h1>
        <form @submit.prevent="login">
          <FormInput
            id="login-username"
            name="login-username"
            v-model="username"
            type="text"
            label-text="Username or Email"
            :error-message="usernameError"
            @input="validateUsername"
          />
          <FormInput
            id="login-password"
            name="login-password"
            v-model="password"
            type="password"
            label-text="Password"
            :error-message="passwordError"
            @input="validatePassword"
          />
          <div class="site-login__meta-container">
            <BaseCheckbox
              id="login-remember"
              name="login-remember"
              v-model="internalRemember"
            >
              Remember me
            </BaseCheckbox>
            <a href="/password/reset" class="site-login__remember-me">
              Forgot your Password?
            </a>
          </div>
          <BaseButton type="submit" :disabled="isSending">LOGIN</BaseButton>
          <FormError :error-message="responseError" />
        </form>
      </div>
    </article>
  </section>
</template>

<style lang="scss" scoped>
  .site-login {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 40px;
    padding: 90px 15px;
    width: 100%;
    &__description {
      p {
        max-width: 300px;
        font-size: 30px;
        font-weight: 700;
        color: var(--itr-blue);
        line-height: 1.5;
      }
    }
    &__article {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      max-width: 450px;

      form {
        display: flex;
        flex-direction: column;
      }
    }
    &__auth-container {
      border-radius: 15px;
      padding: 25px;
      box-shadow: 5px 7px 4px 0px #0000004d;
      background-color: #f0f2f1;
      width: 100%;
    }
    &__meta-container {
      display: flex;
      justify-content: space-between;
      margin-bottom: 25px;
    }
    &__remember-me {
      font-size: 15px;
      color: #a7a9ab;
      font-style: italic;
    }
    :deep(.form-label),
    :deep(.base-checkbox__label) {
      color: var(--itr-charcoal);
    }
  }
  @include screen-md {
    .site-login {
      flex-direction: row;
    }
  }

  :deep(.base-button:disabled),
  :deep(.base-button:hover:disabled) {
    color: #ffffff;
  }

  :deep(.base-checkbox svg .box) {
    fill: #ffffff;
  }
</style>
